import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import s from './WorkTracker.module.scss';
import { taskShape } from './propTypes';
import { api, ApiError } from './api';

const RENEW_MS = 10000;

// Read-only text by default; "Edit description" takes a short-lived lock on
// the task so two people never overwrite each other. A task with no
// description opens straight into the textarea, but still behind the lock:
// two people writing the first description is exactly the collision the lock
// exists for. The typed text is never thrown away -- an expired lock shows a
// message and offers to re-acquire.
export default function DescriptionEditor({ task, onSave, onError }) {
  const hasDescription = (task.description || '').trim().length > 0;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(task.description || '');
  const [held, setHeld] = useState(false); // we hold the lock
  const [notice, setNotice] = useState(null); // { text, warn, reacquire }
  const [saving, setSaving] = useState(false);
  const renew = useRef(null);
  const textareaRef = useRef(null);

  const stopRenewing = () => { if (renew.current) clearInterval(renew.current); renew.current = null; };

  const acquire = async () => {
    try {
      await api.lock(task.id);
      setHeld(true);
      setNotice(null);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Inside the editor the text stays and "Try again" stays with it.
        setNotice({
          text: `${e.body.locked_by} is editing this description right now`,
          warn: true,
          reacquire: editing,
        });
      } else {
        onError(e);
      }
      return false;
    }
  };

  const startEditing = async () => {
    if (!(await acquire())) return;
    setText(task.description || '');
    setEditing(true);
  };

  // No description yet: open the textarea at once (still taking the lock).
  const startedEmpty = useRef(!hasDescription);
  useEffect(() => { if (startedEmpty.current) startEditing(); }, []);

  // Renew while editing; if a renewal is refused the lock went to someone
  // else after ours expired -- keep the text, tell the editor.
  useEffect(() => {
    if (!editing || !held) return undefined;
    renew.current = setInterval(async () => {
      try {
        await api.lock(task.id);
      } catch (e) {
        stopRenewing();
        setHeld(false);
        const taken = e instanceof ApiError && e.status === 409;
        setNotice({
          text: taken
            ? `Your edit lock expired and ${e.body.locked_by} is editing now. Your text is kept.`
            : 'Your edit lock expired. Your text is kept.',
          warn: true,
          reacquire: true,
        });
      }
    }, RENEW_MS);
    return stopRenewing;
  }, [editing, held, task.id]);

  // Release on unmount (drawer closed) if we hold the lock.
  useEffect(() => () => { stopRenewing(); if (held) api.unlock(task.id).catch(() => {}); }, [held, task.id]);

  useEffect(() => { if (editing) textareaRef.current?.focus(); }, [editing]);

  const cancel = async () => {
    stopRenewing();
    if (held) { await api.unlock(task.id).catch(() => {}); setHeld(false); }
    setEditing(false);
    setNotice(null);
    setText(task.description || '');
  };

  // A save always goes out holding the lock: if ours lapsed, get it back
  // first (keeping the text either way) and only then write.
  const save = async () => {
    if (!held && !(await acquire())) return;
    setSaving(true);
    const ok = await onSave(text, true);
    setSaving(false);
    if (ok) { stopRenewing(); setHeld(false); setEditing(false); setNotice(null); }
  };

  if (!editing) {
    return (
      <div className={s.section}>
        <span className={s.label}>Description</span>
        {hasDescription
          ? <p className={s.description}>{task.description}</p>
          : <p className={`${s.description} ${s.descriptionEmpty}`}>No description yet.</p>}
        <button type="button" className={s.btnQuiet} onClick={startEditing}>✎ Edit description</button>
        {notice && <p className={notice.warn ? s.noticeWarn : s.notice}>{notice.text}</p>}
      </div>
    );
  }

  return (
    <div className={s.section}>
      <label className={s.label} htmlFor="wt-description">Description</label>
      <textarea
        id="wt-description"
        ref={textareaRef}
        className={s.textarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What is this about?"
      />
      {notice && (
        <p className={notice.warn ? s.noticeWarn : s.notice}>
          {notice.text}
          {notice.reacquire && (
            <>
              {' '}
              <button type="button" className={s.linkBtn} onClick={acquire}>Try again</button>
            </>
          )}
        </p>
      )}
      <div className={`${s.inline}`} style={{ marginTop: 8 }}>
        <button type="button" className={s.btnPrimary} disabled={saving} onClick={save}>Save description</button>
        {hasDescription && <button type="button" className={s.btn} onClick={cancel}>Cancel</button>}
      </div>
    </div>
  );
}

DescriptionEditor.propTypes = {
  task: taskShape.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};
